import React from 'react';
import Bugsnag, { Event } from '@bugsnag/js';
import bugsnagPluginExpress from '@bugsnag/plugin-express';
import BugsnagPluginReact, {
  BugsnagErrorBoundary as BugsnagErrorBoundaryType
} from '@bugsnag/plugin-react';
import { version, bugsnagKey, environment } from '~/config/public/environment';
import store from '~/config/store';
import { CHUNK_LOAD_ERROR_NAME, INVALID_STATE_ERROR } from '~/config/constants';
import {
  isEmailVerified,
  isMemberOrganisationPersonSelector,
  isPhoneNumberVerifiedSelector,
  memberIdSelector,
  memberLevel
} from '~/App/shared/selectors/sessionSelector';
import { isConnectedSelector } from '~/App/shared/selectors/connectionSelector';
import localforage from 'localforage';
import { getIsWebsocketSupported } from '~/helpers/websocket';
import { featureFlagsSelector } from '~/App/shared/selectors/featureFlagsSelector';

interface ExtendedEvent extends Event {
  originalError: Error | undefined;
}

/**
 * Returns true when an error has a stacktrace but none of its frames point at a
 * JS asset we ship. These originate from browser extensions / injected
 * third-party inline scripts (whose only frame is the HTML document), not our
 * own bundle, which always symbolicates to static/js/*.js via uploaded source
 * maps. See BugSnag error 6938115d1b029fa605c1e9e4 ("t is not a function").
 */
const isNonApplicationError = (event: ExtendedEvent): boolean => {
  const stacktrace = event.errors?.[0]?.stacktrace ?? [];
  if (stacktrace.length === 0) {
    return false; /* keep: no stack to judge on */
  }

  const hasAppFrame = stacktrace.some(
    frame =>
      typeof frame.file === 'string' &&
      /\.(m?js|jsx|tsx?)(\?|#|$)/.test(frame.file)
  );

  return !hasAppFrame;
};

const handleClientError = async (event: ExtendedEvent) => {
  /* Drop injected/third-party global errors that have no frame in our bundle */
  if (event.unhandled && isNonApplicationError(event)) {
    return false;
  }

  const state = store.getState();
  const userId = memberIdSelector(state);
  const error: Error | undefined = event.originalError;

  /* Mark as handled & update severity as info */
  if (error?.name === CHUNK_LOAD_ERROR_NAME) {
    event.unhandled = false;
    event.severity = 'info';
  }

  if (error?.name === INVALID_STATE_ERROR) {
    event.unhandled = false;
    await localforage.clear();
  }

  if (userId) {
    event.setUser(userId);

    event.addMetadata('user', {
      memberLevel: memberLevel(state),
      isEmailVerified: isEmailVerified(state),
      isPhoneNumberVerified: isPhoneNumberVerifiedSelector(state),
      isMemberOrganisationPerson: isMemberOrganisationPersonSelector(state)
    });

    const flags = featureFlagsSelector(state);

    event.addMetadata('featureFlags', flags);
  }

  /* Misc */
  event.addMetadata('misc', {
    isWebsocketSupported: getIsWebsocketSupported(),
    isConnected: isConnectedSelector(state)
  });

  event.addMetadata('impersonating', {
    isImpersonating: Boolean(state?.session?.isImpersonating)
  });
};

if (typeof window !== 'undefined') {
  /* Client */
  Bugsnag.start({
    apiKey: bugsnagKey,
    appVersion: version || 'Local',
    releaseStage: environment ?? 'local',
    plugins: [new BugsnagPluginReact()],
    onError: handleClientError
  });
} else {
  /* Server */
  Bugsnag.start({
    apiKey: bugsnagKey,
    releaseStage: environment ?? 'local',
    appVersion: version || 'Local',
    logger: null,
    plugins: [bugsnagPluginExpress]
  });
}

const ReactPlugin = Bugsnag.getPlugin('react');
export const BugsnagErrorBoundary = ReactPlugin?.createErrorBoundary(
  React
) as BugsnagErrorBoundaryType;
